import { createLineSwap, paintFailure } from './cart-line-swap.js';
import { originOf, withOrigin } from './addon-origin.js';

const MIN_QUERY = 2;
const TYPEAHEAD_MS = 500;
const RESULTS = 5;

export function createCartMusic({ root, cart, sheet, host, createSearch, fetch, delay, track, notify, pathname }) {
  const drawer = root.getElementById('cart-drawer');
  if (!drawer || !cart) return null;

  const addUrl = drawer.dataset.cartAddUrl;
  const changeUrl = drawer.dataset.cartChangeUrl;
  const copy = {
    added: drawer.dataset.noticeAdded || '',
    removed: drawer.dataset.noticeRemoved || '',
  };
  const errorNode = drawer.querySelector('[data-cart-error]');
  const genericError = errorNode ? (errorNode.dataset.errorGeneric || '') : '';
  const sheetNode = root.querySelector('[data-cart-sheet]');
  const sheetCopy = {
    remove: (sheetNode && sheetNode.dataset.sheetRemove) || '',
    later: (sheetNode && sheetNode.dataset.sheetLater) || '',
  };

  const searches = new Map();
  const searchFor = (spotify) => {
    const key = (spotify && spotify.clientId) || '';
    if (!searches.has(key)) searches.set(key, createSearch({ ...(spotify || {}), fetch }));
    return searches.get(key);
  };

  async function loadConfig(productUrl) {
    const productDocument = await host.loadProductDocument(productUrl);
    const island = productDocument.getElementById('pdp-config');
    if (!island) throw new Error('cart-music: product page carries no #pdp-config');
    const config = JSON.parse(island.textContent);
    return { strings: config.strings || {}, spotify: config.spotify || {} };
  }

  const el = (tag, attributes, ...children) => {
    const node = root.createElement(tag);
    Object.keys(attributes || {}).forEach((name) => {
      const value = attributes[name];
      if (value === false || value === null || value === undefined) return;
      if (name === 'text') node.textContent = value;
      else if (name === 'hidden') node.hidden = true;
      else if (name === 'disabled') node.disabled = true;
      else node.setAttribute(name, value === true ? '' : String(value));
    });
    node.append(...children.filter(Boolean));
    return node;
  };

  const splitLabel = (label) => {
    const parts = String(label || '').split(' — ');
    return { name: parts[0] || '', artist: parts.slice(1).join(' — ') };
  };

  const liveCard = (group) => {
    const accordion = Array.from(drawer.querySelectorAll('[data-upsell]')).find((node) => node.dataset.group === group);
    return accordion ? accordion.querySelector('[data-offer="musica"] .cart-upsell__cta') : null;
  };

  async function post(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: 'cart-drawer', sections_url: pathname }),
    });
    if (!response.ok) throw new Error('cart-music: write failed');
    return response.json();
  }

  const cartJsonUrl = (drawer.dataset.cartUrl || '/cart') + '.js';

  async function readCart() {
    const response = await fetch(cartJsonUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart-music: cart read failed');
    return response.json();
  }

  const swapOut = createLineSwap({ post, changeUrl, readCart });

  const render = (data) => {
    if (!cart.replaceContent(data && data.sections && data.sections['cart-drawer'])) cart.reload();
  };

  function buildPopup(trigger, config) {
    const data = trigger.dataset;
    const strings = config.strings;
    const accordion = trigger.closest('[data-upsell]');
    const family = accordion ? accordion.dataset.family : '';
    const group = data.group;
    const added = !!data.lineKey;

    const state = {
      selected: added ? { ...splitLabel(data.musicTitle), image: data.musicImage || '', url: data.musicUrl || '' } : null,
      seq: 0,
      cancelTypeahead: null,
    };

    const input = el('input', {
      class: 'cart-music__input', type: 'text', autocomplete: 'off', autocapitalize: 'off',
      placeholder: strings.spotifySearchPlaceholder || '', 'aria-label': strings.spotifySearchLabel || '',
    });
    const searchButton = el('button', { type: 'button', class: 'cart-music__search', 'aria-label': strings.spotifySearchLabel || '' },
      el('span', { class: 'cart-music__search-icon', 'aria-hidden': 'true', text: '⌕' }));
    const status = el('p', { class: 'cart-music__status', role: 'status', hidden: true });
    const list = el('ul', { class: 'cart-music__list', role: 'list', hidden: true });
    const selectedLabel = el('p', { class: 'cart-music__selected-label', text: strings.spotifySelectedLabel || '', hidden: true });
    const selectedCover = el('img', { class: 'cart-music__cover', alt: '', width: '40', height: '40' });
    const selectedName = el('span', { class: 'cart-music__name' });
    const selectedArtist = el('span', { class: 'cart-music__artist' });
    const clearButton = el('button', { type: 'button', class: 'cart-music__clear', text: strings.spotifyRemoveShort || '', 'aria-label': strings.spotifyRemove || '' });
    const selected = el('div', { class: 'cart-music__selected', hidden: true },
      selectedCover, el('span', { class: 'cart-music__meta' }, selectedName, selectedArtist), clearButton);
    const cta = el('button', { type: 'button', class: 'cart-sheet__cta', 'data-music-cta': true, disabled: true, text: added ? (data.ctaReplace || '') : (data.ctaAdd || '') });
    const secondary = el('button', { type: 'button', class: 'cart-sheet__later', 'data-music-secondary': true, text: added ? sheetCopy.remove : sheetCopy.later });

    const popup = el('div', { class: 'cart-music' },
      data.sheetDesc ? el('p', { class: 'cart-music__hint', text: data.sheetDesc }) : null,
      el('p', { class: 'cart-music__label', text: strings.spotifySearchLabel || '' }),
      el('div', { class: 'cart-music__row' }, input, searchButton),
      status, list, selectedLabel, selected, cta, secondary);

    const setStatus = (text) => {
      status.textContent = text || '';
      status.hidden = !text;
    };

    const paintSelected = () => {
      const on = !!state.selected;
      selectedLabel.hidden = !on;
      selected.hidden = !on;
      cta.disabled = !on;
      if (on) {
        selectedName.textContent = state.selected.name;
        selectedArtist.textContent = state.selected.artist;
        if (state.selected.image) selectedCover.setAttribute('src', state.selected.image);
        else selectedCover.removeAttribute('src');
        selectedCover.hidden = !state.selected.image;
      }
    };

    const paintResults = (tracks) => {
      list.replaceChildren(...tracks.map((item) => {
        const row = el('button', { type: 'button', class: 'cart-music__result', 'aria-label': (strings.spotifyAdd || '') + ': ' + item.name + ' — ' + item.artist },
          item.image ? el('img', { class: 'cart-music__cover', src: item.image, alt: '', width: '40', height: '40', loading: 'lazy' }) : el('span', { class: 'cart-music__cover cart-music__cover--empty', 'aria-hidden': 'true' }),
          el('span', { class: 'cart-music__meta' }, el('span', { class: 'cart-music__name', text: item.name }), el('span', { class: 'cart-music__artist', text: item.artist })),
          el('span', { class: 'cart-music__add', 'aria-hidden': 'true', text: '+' }));
        row.addEventListener('click', () => pick(item));
        return el('li', { class: 'cart-music__item' }, row);
      }));
      list.hidden = tracks.length === 0;
    };

    function pick(item) {
      state.selected = { name: item.name, artist: item.artist, image: item.image || '', url: item.url || '' };
      state.seq += 1;
      paintResults([]);
      setStatus('');
      paintSelected();
    }

    function clearPick() {
      state.selected = null;
      input.value = '';
      paintResults([]);
      setStatus('');
      paintSelected();
    }

    async function runSearch(query) {
      const seq = ++state.seq;
      setStatus(strings.spotifyLoading || '');
      try {
        const tracks = await searchFor(config.spotify)(query, RESULTS);
        if (seq !== state.seq) return;
        paintResults(tracks);
        setStatus(tracks.length ? '' : (strings.spotifyEmpty || ''));
      } catch {
        if (seq !== state.seq) return;
        paintResults([]);
        setStatus(strings.spotifyError || genericError);
      }
    }

    const cancelTypeahead = () => {
      if (state.cancelTypeahead) state.cancelTypeahead();
      state.cancelTypeahead = null;
    };

    const doSearch = () => {
      cancelTypeahead();
      const query = (input.value || '').trim();
      if (query.length < MIN_QUERY) return;
      if (state.selected) { state.selected = null; paintSelected(); }
      runSearch(query);
    };

    input.addEventListener('input', () => {
      if (state.selected) { state.selected = null; paintSelected(); }
      cancelTypeahead();
      const query = (input.value || '').trim();
      if (query.length < MIN_QUERY) {
        state.seq += 1;
        paintResults([]);
        setStatus('');
        return;
      }
      state.cancelTypeahead = delay(() => runSearch(query), TYPEAHEAD_MS);
    });
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      doSearch();
    });
    searchButton.addEventListener('click', doSearch);
    clearButton.addEventListener('click', clearPick);

    cta.addEventListener('click', () => {
      if (!state.selected) return;
      const chosen = state.selected;
      sheet.close();
      commit(trigger, chosen, { group, family });
    });
    secondary.addEventListener('click', () => {
      sheet.close();
      if (added) remove(trigger, { group, family });
    });

    paintSelected();
    return { popup, destroy: cancelTypeahead };
  }

  function sameAsLine(card, chosen, label) {
    if (!card.dataset.lineKey) return false;
    if (chosen.url && card.dataset.musicUrl) return chosen.url === card.dataset.musicUrl;
    return label === (card.dataset.musicTitle || '');
  }

  function commit(trigger, chosen, { group, family }) {
    const { variantId, priceCents, noticeSubtitle } = trigger.dataset;
    cart.hideError();
    return cart.enqueueWrite(async () => {
      const card = liveCard(group);
      if (!card) return;
      const oldKey = card.dataset.lineKey || '';
      const label = chosen.artist ? (chosen.name + ' — ' + chosen.artist) : chosen.name;
      if (sameAsLine(card, chosen, label)) return;
      let painted = null;
      try {
        const previous = oldKey ? ((await readCart()).items || []).find((item) => item.key === oldKey) : null;
        const properties = withOrigin(
          {
            _group: group,
            _addon: 'musica',
            'Música': label,
            _spotify_image: chosen.image || '',
            _spotify_url: chosen.url || '',
          },
          previous ? originOf(previous) : 'cart',
        );
        painted = await post(addUrl, { items: [{ id: Number(variantId), quantity: 1, properties }] });
        if (oldKey) painted = await swapOut(oldKey, painted);
        track('cart_upsell_add', { addon: 'musica', value_cents: Number(priceCents) || 0, origin: 'cart', family });
        render(painted);
        if (copy.added) notify({ title: copy.added, subtitle: (noticeSubtitle || '').replace('{track}', chosen.name), group });
      } catch (error) {
        paintFailure({ cart, render }, error, painted);
      }
    });
  }

  function remove(trigger, { group, family }) {
    const { noticeName } = trigger.dataset;
    cart.hideError();
    return cart.enqueueWrite(async () => {
      const card = liveCard(group);
      const key = card && card.dataset.lineKey;
      if (!key) return;
      try {
        const data = await post(changeUrl, { id: key, quantity: 0 });
        track('cart_upsell_remove', { addon: 'musica', family });
        render(data);
        if (copy.removed) notify({ title: copy.removed, subtitle: noticeName || '', group });
      } catch {
        cart.showError();
      }
    });
  }

  async function openPopup(trigger) {
    const token = sheet.stamp();
    const accordion = trigger.closest('[data-upsell]');
    const family = accordion ? accordion.dataset.family : '';
    trigger.setAttribute('aria-busy', 'true');
    let config;
    try {
      config = await loadConfig(trigger.dataset.productUrl);
    } catch {
      config = { strings: {}, spotify: {} };
    } finally {
      trigger.removeAttribute('aria-busy');
    }
    if (!sheet.isCurrent(token)) return;
    const built = buildPopup(trigger, config);
    sheet.openCustom({
      title: trigger.dataset.noticeName || '',
      tag: trigger.dataset.priceFormatted || '',
      opener: trigger,
      sheet: 'musica',
      family,
      group: trigger.dataset.group,
      mountInto: (node) => node.append(built.popup),
      onClose: built.destroy,
    });
    if (!config.spotify.clientId) {
      const status = built.popup.querySelector('.cart-music__status');
      if (status) { status.textContent = genericError; status.hidden = !genericError; }
    }
  }

  drawer.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-action="upsell-music"]');
    if (!trigger || !drawer.contains(trigger)) return;
    event.preventDefault();
    openPopup(trigger);
  });

  return { openPopup };
}

if (typeof document !== 'undefined') {
  const start = async () => {
    const { createSpotifySearch } = await import('./pdp-spotify-search.js');
    createCartMusic({
      root: document,
      cart: window.fnCart,
      sheet: {
        openCustom: (options) => window.fnCartSheet && window.fnCartSheet.openCustom(options),
        close: () => window.fnCartSheet && window.fnCartSheet.close(),
        stamp: () => (window.fnCartSheet ? window.fnCartSheet.stamp() : 0),
        isCurrent: (token) => (window.fnCartSheet ? window.fnCartSheet.isCurrent(token) : true),
      },
      host: {
        loadProductDocument: (url) => (window.fnCartEdit
          ? window.fnCartEdit.loadProductDocument(url)
          : Promise.reject(new Error('cart-music: no edit host'))),
      },
      createSearch: createSpotifySearch,
      fetch: (url, init) => window.fetch(url, init),
      delay: (callback, ms) => {
        const id = setTimeout(callback, ms);
        return () => clearTimeout(id);
      },
      track: (event, payload) => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event, ...payload });
      },
      notify: (payload) => {
        if (window.fnCartNotice) window.fnCartNotice.show(payload);
      },
      pathname: window.location.pathname,
    });
  };
  if (window.fnCart) start();
  else document.addEventListener('fn:cart-ready', start, { once: true });
}
